/**
 * Key transfer — TARGET device (the new one, which has no key yet).
 *
 * Reached from the Restore screen. Under protocol v3 this device speaks first:
 * it generates an ephemeral X25519 key pair, shows the *public* half as QR #1,
 * and keeps the private half in memory only. The source device seals the seed to
 * it and shows QR #2, which this screen scans and opens.
 *
 * ⚠️  `keyPairRef` must never be persisted. It is the only thing that can open
 *     QR #2, and keeping it in memory for the life of the screen is what makes a
 *     photograph of either code worthless.
 *
 * The seed is not committed to storage until the user confirms the 6-digit code
 * matches the source device — that comparison is what catches a substituted QR.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import * as ScreenCapture from 'expo-screen-capture';
import QRCodeView from '../../src/components/QRCodeView';
import type { KeyPair } from 'hpke';
import {
  createTransferRequest,
  openSealedSeed,
  QR_EXPIRY_SECONDS,
} from '../../src/utils/key-transfer';
import { storeSeed } from '../../src/utils/crypto';
import { deriveX25519KeyPair } from '../../src/utils/teamCrypto';
import { persistPersonalTeamKey, syncTeamKeys } from '../../src/utils/teamKeySync';
import { useAuthStore } from '../../src/stores/authStore';
import { useEncryptionStore } from '../../src/stores/encryptionStore';
import QRScannerView from '../../src/components/QRScannerView';
import { useTheme } from '../../src/theme/ThemeContext';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

type Step = 'showing' | 'scanning' | 'confirming';

export default function TransferTargetScreen() {
  const router = useRouter();
  const [step, setStep] = useState<Step>('showing');
  const [qrValue, setQrValue] = useState<string | null>(null);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(QR_EXPIRY_SECONDS);
  const [expired, setExpired] = useState(false);

  const keyPairRef = useRef<KeyPair | null>(null);
  const seedRef = useRef<Uint8Array | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scannedRef = useRef(false);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const generateRequest = useCallback(async () => {
    setError(null);
    setExpired(false);
    setQrValue(null);
    setSecondsLeft(QR_EXPIRY_SECONDS);
    scannedRef.current = false;

    try {
      const request = await createTransferRequest();
      keyPairRef.current = request.keyPair;

      setQrValue(request.qr);

      clearTimer();
      timerRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearTimer();
            setQrValue(null);
            setExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Failed to start transfer:', err);
      setError('Could not start the transfer. Please try again.');
    }
  }, []);

  useEffect(() => {
    generateRequest();
    return clearTimer;
  }, [generateRequest]);

  // Block screenshots while a code is on screen. Native only — the web build has
  // no equivalent, so a browser tab is not covered by this.
  useEffect(() => {
    if (Platform.OS === 'web' || step === 'scanning') return;
    ScreenCapture.preventScreenCaptureAsync().catch(() => {});
    return () => {
      ScreenCapture.allowScreenCaptureAsync().catch(() => {});
    };
  }, [step]);

  const handleScan = useCallback(async (data: string) => {
    if (scannedRef.current || isProcessing) return;
    const keyPair = keyPairRef.current;
    if (!keyPair) {
      setScanError('This transfer expired. Go back and start again.');
      return;
    }

    scannedRef.current = true;
    setIsProcessing(true);
    setScanError(null);

    try {
      const opened = await openSealedSeed(data, keyPair);
      seedRef.current = opened.seed;
      setConfirmationCode(opened.confirmationCode);
      clearTimer();
      setStep('confirming');
    } catch (err) {
      console.error('Transfer scan failed:', err);
      setScanError(
        err instanceof Error
          ? err.message
          : 'Invalid QR code. Please scan the code shown on your other device.'
      );
      scannedRef.current = false;
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing]);

  const handleConfirm = useCallback(async () => {
    const seed = seedRef.current;
    if (!seed) return;

    setIsProcessing(true);
    setError(null);

    try {
      const userId = useAuthStore.getState().user?.id;
      if (!userId) {
        throw new Error('You are not signed in. Sign in and start the transfer again.');
      }
      await storeSeed(seed, userId);

      const { privateKey: x25519PrivateKey } = deriveX25519KeyPair(seed);
      await syncTeamKeys(x25519PrivateKey);

      // ⚠️  The seed is not a working device. `getKeyForTeam` resolves personal
      //     content to the persisted device key, and until this lands there is
      //     none — the flags below would send the user into an app that cannot
      //     decrypt anything and, after a reload, straight back to the restore
      //     screen. That is what this screen did until 2026-09, on every
      //     platform, and it is the reason the transfer "did not save the key".
      const stored = await persistPersonalTeamKey(userId);
      if (!stored) {
        throw new Error(
          'The key was received, but your account key could not be fetched from the server. Check your connection and tap "The Codes Match" again.'
        );
      }

      useEncryptionStore.getState().setHasLocalKey(true);
      useEncryptionStore.getState().setE2EEComplete(true);
      // Navigation handled by root layout when e2eeSetupComplete changes
    } catch (err) {
      console.error('Failed to store transferred key:', err);
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not save the key on this device. Please try again.'
      );
      setIsProcessing(false);
    }
  }, []);

  const formatTime = (s: number) => {
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  if (step === 'scanning') {
    return (
      <QRScannerView
        title="Scan Their Code"
        subtitle="Point your camera at the QR code now showing on your other device."
        onScan={handleScan}
        onBack={() => setStep('showing')}
        isProcessing={isProcessing}
        processingLabel="Reading key..."
        error={scanError}
        onDismissError={() => {
          setScanError(null);
          scannedRef.current = false;
        }}
        testIDPrefix="transfer-target"
      />
    );
  }

  if (step === 'confirming') {
    return (
      <View style={styles.container}>
        <View style={styles.card}>
          <View style={styles.iconContainer}>
            <Text style={styles.iconText}>{'\u{1F511}'}</Text>
          </View>
          <Text style={styles.title}>Check the Code</Text>
          <Text style={styles.subtitle}>
            Your other device is showing a 6-digit code. Confirm it matches this
            one before continuing.
          </Text>

          <View style={styles.codeBox}>
            <Text style={styles.codeValue} testID="transfer-target.confirmation-code">
              {confirmationCode}
            </Text>
          </View>

          {error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleConfirm}
            disabled={isProcessing}
            activeOpacity={0.7}
            accessibilityLabel="Codes match, finish transfer"
            accessibilityRole="button"
            testID="transfer-target.confirm-button"
          >
            {isProcessing ? (
              <ActivityIndicator size="small" color={colors.textOnPrimary} />
            ) : (
              <Text style={styles.primaryButtonText}>The Codes Match</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => {
              seedRef.current = null;
              setConfirmationCode(null);
              setStep('showing');
              generateRequest();
            }}
            disabled={isProcessing}
            activeOpacity={0.7}
            accessibilityLabel="Codes do not match, start over"
            accessibilityRole="button"
            testID="transfer-target.reject-button"
          >
            <Text style={styles.secondaryButtonText}>They Don&apos;t Match</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <View style={styles.iconContainer}>
          <Text style={styles.iconText}>{'\u{1F4F2}'}</Text>
        </View>
        <Text style={styles.title}>Transfer Key</Text>
        <Text style={styles.subtitle}>
          On your other device, open Settings and tap &quot;Transfer Key to New
          Device&quot;, then scan this code.
        </Text>

        <View style={styles.qrContainer}>
          {!qrValue && !error && !expired && (
            <ActivityIndicator size="large" color={colors.primary} />
          )}
          {qrValue && (
            <>
              <QRCodeView value={qrValue} size={280} testID="transfer-target.qr-image" />
              <Text style={styles.timer}>Expires in {formatTime(secondsLeft)}</Text>
            </>
          )}
          {expired && (
            <View style={styles.expiredBox}>
              <Text style={styles.expiredText}>This code expired.</Text>
              <TouchableOpacity
                style={styles.regenerateButton}
                onPress={generateRequest}
                activeOpacity={0.7}
                accessibilityLabel="Generate new QR code"
                accessibilityRole="button"
                testID="transfer-target.regenerate-button"
              >
                <Text style={styles.regenerateButtonText}>Generate New Code</Text>
              </TouchableOpacity>
            </View>
          )}
          {error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}
        </View>

        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => setStep('scanning')}
          disabled={!qrValue}
          activeOpacity={0.7}
          accessibilityLabel="Next, scan the code on the other device"
          accessibilityRole="button"
          testID="transfer-target.next-button"
        >
          <Text style={styles.primaryButtonText}>Next: Scan Their Code</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={() => router.back()}
          activeOpacity={0.7}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          testID="transfer-target.back-button"
        >
          <Text style={styles.secondaryButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1 as const,
    backgroundColor: theme.colors.brandedBackground,
    justifyContent: 'center' as const,
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.primaryBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    marginBottom: 16,
  },
  iconText: { fontSize: 28 },
  title: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    textAlign: 'center' as const,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  qrContainer: {
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 300,
    marginBottom: 16,
  },
  timer: {
    fontSize: 14,
    color: theme.colors.textMuted,
    fontWeight: '600' as const,
    marginTop: 12,
  },
  expiredBox: {
    alignItems: 'center' as const,
  },
  expiredText: {
    fontSize: 14,
    color: theme.colors.errorText,
    marginBottom: 12,
    textAlign: 'center' as const,
  },
  regenerateButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  regenerateButtonText: {
    color: theme.colors.textOnPrimary,
    fontSize: 14,
    fontWeight: '600' as const,
  },
  codeBox: {
    borderWidth: 1,
    borderColor: theme.colors.borderMedium,
    borderRadius: 10,
    paddingVertical: 20,
    marginBottom: 20,
    alignItems: 'center' as const,
  },
  codeValue: {
    fontSize: 34,
    fontWeight: '700' as const,
    letterSpacing: 8,
    color: theme.colors.textPrimary,
  },
  errorBox: {
    backgroundColor: theme.colors.errorBg,
    borderWidth: 1,
    borderColor: theme.colors.errorBorder,
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
  },
  errorText: {
    color: theme.colors.errorText,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center' as const,
  },
  primaryButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
    marginBottom: 10,
  },
  primaryButtonText: {
    color: theme.colors.textOnPrimary,
    fontSize: 16,
    fontWeight: '700' as const,
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: theme.colors.borderMedium,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center' as const,
  },
  secondaryButtonText: {
    color: theme.colors.textSecondary,
    fontSize: 16,
    fontWeight: '600' as const,
  },
});
